import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import type { OrderStatus } from "@/types";
import {
  getOrderStatusConfig,
  ORDER_SOURCE_CONFIG,
  PAYMENT_CONFIG,
  PICKUP_CONFIG,
} from "@/constants/status";
import { formatSAR } from "@/utils/format";
import { downloadCsv } from "@/utils/csv";
import OrderFilters from "./components/OrderFilters";
import OrdersTable from "./components/OrdersTable";

export default function OrdersPage() {
  const navigate = useNavigate();
  const { orders: list } = useStore();
  const [filter, setFilter] = useState<OrderStatus | "all">("all");
  const [query, setQuery] = useState("");
  const [branch, setBranch] = useState("all");
  const [exported, setExported] = useState<number | null>(null);

  const branchOptions = useMemo(
    () => Array.from(new Set(list.map((order) => order.branch))),
    [list],
  );

  const counts = useMemo(() => {
    const result: Record<string, number> = { all: list.length };
    list.forEach((order) => {
      result[order.status] = (result[order.status] ?? 0) + 1;
    });
    return result;
  }, [list]);

  const visible = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return list.filter((order) => {
      const matchStatus = filter === "all" || order.status === filter;
      const matchBranch = branch === "all" || order.branch === branch;
      const matchQuery =
        keyword === "" ||
        order.id.includes(keyword) ||
        order.customer.toLowerCase().includes(keyword);
      return matchStatus && matchBranch && matchQuery;
    });
  }, [list, filter, branch, query]);

  const activeTotal = useMemo(
    () =>
      visible.reduce(
        (sum, order) =>
          ["new", "preparing", "almost", "ready", "outForDelivery"].includes(
            order.status,
          )
            ? sum + order.amount
            : sum,
        0,
      ),
    [visible],
  );

  const handleExport = () => {
    const headers = [
      "رقم الطلب",
      "العميل",
      "الجوال",
      "الفرع",
      "المصدر",
      "وقت الطلب",
      "عدد المنتجات",
      "المبلغ (ر.س)",
      "رسوم التطبيق (ر.س)",
      "رسوم التوصيل (ر.س)",
      "الحالة",
      "طريقة الاستلام",
      "طريقة الدفع",
    ];
    const rows = visible.map((order) => [
      order.id,
      order.customer,
      order.phone,
      order.branch,
      ORDER_SOURCE_CONFIG[order.source].label,
      order.date,
      order.items,
      order.amount.toFixed(2),
      order.appFee.toFixed(2),
      (order.deliveryFee ?? 0).toFixed(2),
      getOrderStatusConfig(order.status, order.pickup).label,
      PICKUP_CONFIG[order.pickup].label,
      PAYMENT_CONFIG[order.payment].label,
    ]);
    downloadCsv("orders.csv", headers, rows);
    setExported(rows.length);
    window.setTimeout(() => setExported(null), 2500);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
          <div className="flex items-center gap-2 text-foreground-500">
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-file-list-3-line"></i>
            </span>
            <span className="text-sm">إجمالي الطلبات</span>
          </div>
          <p className="mt-3 text-3xl font-bold text-foreground-950">
            {list.length}
          </p>
        </div>
        <div className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
          <div className="flex items-center gap-2 text-foreground-500">
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-800">
              <i className="ri-flashlight-line"></i>
            </span>
            <span className="text-sm">طلبات نشطة</span>
          </div>
          <p className="mt-3 text-3xl font-bold text-foreground-950">
            {counts.new ?? 0}
            <span className="mx-1 text-sm font-medium text-foreground-400">
              جديد
            </span>
          </p>
        </div>
        <div className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
          <div className="flex items-center gap-2 text-foreground-500">
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-800">
              <i className="ri-money-dollar-circle-line"></i>
            </span>
            <span className="text-sm">قيمة الطلبات الظاهرة</span>
          </div>
          <p className="mt-3 text-3xl font-bold text-foreground-950">
            {formatSAR(activeTotal)}
          </p>
        </div>
      </div>

      <section className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
        <OrderFilters
          filter={filter}
          counts={counts}
          onFilterChange={setFilter}
          query={query}
          onQueryChange={setQuery}
          branch={branch}
          onBranchChange={setBranch}
          branchOptions={branchOptions}
        />
      </section>

      <section className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-bold text-foreground-950">
            قائمة الطلبات
          </h2>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600">
              {visible.length} طلب
            </span>
            <button
              type="button"
              onClick={handleExport}
              disabled={visible.length === 0}
              className="inline-flex items-center gap-2 h-9 rounded-full bg-primary-500 px-4 text-xs font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-download-2-line"></i>
              تصدير CSV
            </button>
            {exported !== null && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700">
                <i className="ri-checkbox-circle-line"></i>
                تم تصدير {exported} طلب
              </span>
            )}
          </div>
        </div>
        <OrdersTable
          orders={visible}
          onSelect={(order) => navigate(`/orders/${order.id}`)}
        />
      </section>
    </div>
  );
}